import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { IssueCreate, IssueDetail, IssueSummary, Project } from '@jisy/shared';
import { ApiRequestError } from '../api/client.js';
import { createOptionsQuery, useIssueCreate } from '../api/queries.js';
import { AdfEditor, useAdfEditor } from './AdfEditor.js';
import { ParentPicker } from './ParentPicker.js';
import styles from './CreateIssueModal.module.css';

/**
 * Форма создания задачи.
 *
 * Заполняются те же поля, что правятся в карточке. Экран создания в Jira бывает шире: если
 * тип требует полей, которых здесь нет, форма не пытается угадать значения, а говорит, каких
 * полей не хватает, и предлагает завести задачу в Jira.
 */
export function CreateIssueModal({
  projects,
  initialProjectId,
  siteUrl,
  onClose,
  onCreated,
}: {
  projects: Project[];
  /** Раздел, выбранный в таблице. В режиме «Все разделы» — null, и его выбирают в форме. */
  initialProjectId: string | null;
  siteUrl: string;
  onClose: () => void;
  onCreated: (issue: IssueDetail) => void;
}): React.ReactElement {
  const { t } = useTranslation();

  const [projectId, setProjectId] = useState(initialProjectId ?? '');
  const [issueTypeId, setIssueTypeId] = useState('');
  const [summary, setSummary] = useState('');
  const [priorityId, setPriorityId] = useState('');
  const [assigneeAccountId, setAssigneeAccountId] = useState('');
  const [duedate, setDuedate] = useState('');
  const [progress, setProgress] = useState('');
  /** Родитель подзадачи. Спрашивается только у подзадачи — у Jira он для неё обязателен. */
  const [parent, setParent] = useState<IssueSummary | null>(null);

  const editor = useAdfEditor(null);
  const create = useIssueCreate();

  const options = useQuery(createOptionsQuery(projectId || null, issueTypeId || null));

  // Тип обязателен, поэтому подставляется сразу, как пришёл список. Первым в схеме проекта
  // нередко стоит эпик или подзадача — по умолчанию берём обычную задачу, остальное пусть
  // выбирают осознанно.
  const issueTypes = options.data?.issueTypes ?? [];
  useEffect(() => {
    if (issueTypeId !== '' || issueTypes.length === 0) return;
    const ordinary = issueTypes.find(
      (type) => !type.subtask && (type.hierarchyLevel === 0 || type.hierarchyLevel === null),
    );
    setIssueTypeId((ordinary ?? issueTypes[0])?.id ?? '');
  }, [issueTypes, issueTypeId]);

  const subtask = issueTypes.some((type) => type.id === issueTypeId && type.subtask);

  const unsupported = options.data?.requiredUnsupported ?? [];
  const blocked = unsupported.length > 0;

  const error = create.error instanceof ApiRequestError ? create.error : null;

  function submit(): void {
    if (projectId === '' || issueTypeId === '' || summary.trim() === '') return;
    if (subtask && parent === null) return;

    const description = editor.dirty ? editor.toAdf() : null;
    const input: IssueCreate = {
      projectId,
      issueTypeId,
      ...(subtask && parent ? { parentKey: parent.key } : {}),
      summary: summary.trim(),
      ...(description === null ? {} : { description }),
      ...(priorityId === '' ? {} : { priorityId }),
      ...(assigneeAccountId === '' ? {} : { assigneeAccountId }),
      ...(duedate === '' ? {} : { duedate }),
      // Поле ввода числа не мешает набрать 150 руками, а сервер такое значение не примет.
      ...(progress === '' ? {} : { progress: clampProgress(Number(progress)) }),
    };

    create.mutate(input, { onSuccess: (issue) => onCreated(issue) });
  }

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content
          className={styles.modal}
          aria-describedby={undefined}
          onEscapeKeyDown={(event) => {
            // Открытый список родителей закрывается по Esc сам, форма остаётся.
            if (event.target instanceof Element && event.target.closest('[data-escape-local]')) {
              event.preventDefault();
            }
          }}
        >
          <header className={styles.header}>
            <Dialog.Title className={styles.title}>{t('create.title')}</Dialog.Title>
            <div className={styles.spacer} />
            <Dialog.Close asChild>
              <button type="button" className={styles.closeButton} aria-label={t('issue.close')}>
                ✕
              </button>
            </Dialog.Close>
          </header>

          <div className={styles.body}>
            <div className={styles.row}>
              <label className={styles.field}>
                <span className="label">{t('table.columns.section')}</span>
                <select
                  className={styles.select}
                  value={projectId}
                  onChange={(event) => {
                    setProjectId(event.target.value);
                    // Типы принадлежат схеме раздела, а исполнителя Jira разрешает не в каждом
                    // проекте: обе величины относятся к прежнему разделу и переносу не подлежат.
                    // Родитель тоже: подзадача живёт в разделе своей задачи.
                    setIssueTypeId('');
                    setAssigneeAccountId('');
                    setParent(null);
                  }}
                >
                  <option value="">{t('create.chooseSection')}</option>
                  {projects.map((project) => (
                    <option value={project.id} key={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className={styles.field}>
                <span className="label">{t('table.columns.type')}</span>
                <select
                  className={styles.select}
                  value={issueTypeId}
                  disabled={projectId === '' || issueTypes.length === 0}
                  onChange={(event) => {
                    setIssueTypeId(event.target.value);
                    // Родитель есть только у подзадачи: у другого типа он просто не нужен.
                    if (!issueTypes.some((type) => type.id === event.target.value && type.subtask)) {
                      setParent(null);
                    }
                  }}
                >
                  {issueTypes.length === 0 && <option value="">{t('create.typesUnavailable')}</option>}
                  {issueTypes.map((type) => (
                    <option value={type.id} key={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {subtask && projectId !== '' && (
              <div className={styles.field}>
                <span className="label">{t('create.parent')}</span>
                <ParentPicker projectId={projectId} value={parent} onChange={setParent} />
                {parent === null && <span className={styles.hint}>{t('create.parentHint')}</span>}
              </div>
            )}

            <label className={styles.field}>
              <span className="label">{t('table.columns.summary')}</span>
              <input
                className={styles.input}
                value={summary}
                onChange={(event) => setSummary(event.target.value)}
                placeholder={t('create.summaryPlaceholder')}
                maxLength={255}
                autoFocus
              />
            </label>

            <div className={styles.field}>
              <span className="label">{t('issue.description')}</span>
              <AdfEditor
                value={editor.document}
                onChange={editor.setDocument}
                placeholder={t('issue.descriptionPlaceholder')}
              />
            </div>

            <div className={styles.row}>
              <label className={styles.field}>
                <span className="label">{t('table.columns.assignee')}</span>
                <select
                  className={styles.select}
                  value={assigneeAccountId}
                  disabled={(options.data?.readOnly ?? []).includes('assignee')}
                  onChange={(event) => setAssigneeAccountId(event.target.value)}
                >
                  <option value="">{t('table.unassigned')}</option>
                  {(options.data?.assignees ?? []).map((person) => (
                    <option value={person.accountId} key={person.accountId}>
                      {person.displayName}
                    </option>
                  ))}
                </select>
              </label>

              <label className={styles.field}>
                <span className="label">{t('table.columns.priority')}</span>
                <select
                  className={styles.select}
                  value={priorityId}
                  onChange={(event) => setPriorityId(event.target.value)}
                >
                  {/* Пустое значение — приоритет по умолчанию из схемы проекта. */}
                  <option value="">{t('create.defaultValue')}</option>
                  {(options.data?.priorities ?? []).map((priority) => (
                    <option value={priority.id} key={priority.id}>
                      {priority.rank ? `P${priority.rank} · ${priority.name}` : priority.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className={styles.row}>
              <label className={styles.field}>
                <span className="label">{t('table.columns.duedate')}</span>
                <input
                  type="date"
                  className={`mono ${styles.dateInput}`}
                  value={duedate}
                  onChange={(event) => setDuedate(event.target.value)}
                />
              </label>

              {options.data?.hasProgressField === true && (
                <label className={styles.field}>
                  <span className="label">{t('issue.progress')}</span>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={5}
                    className={`mono ${styles.input}`}
                    value={progress}
                    onChange={(event) => setProgress(event.target.value)}
                    placeholder="0"
                  />
                </label>
              )}
            </div>

            {blocked && (
              <div className={styles.warning} role="alert">
                <span className={styles.warningTitle}>{t('create.requiredTitle')}</span>
                <p className={styles.warningBody}>
                  {t('create.requiredBody', {
                    fields: unsupported.map((field) => field.name).join(', '),
                  })}
                </p>
                <a
                  className={styles.warningLink}
                  href={`${siteUrl}/secure/CreateIssue!default.jspa`}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  {t('create.openJira')}
                </a>
              </div>
            )}

            {error && !blocked && (
              <div className={styles.error} role="alert">
                {error.code === 'JIRA_REQUIRED_FIELDS'
                  ? t('create.requiredBody', { fields: String(error.params?.['fields'] ?? '') })
                  : t(`errors.${error.code}`)}
              </div>
            )}
          </div>

          <footer className={styles.footer}>
            <div className={styles.spacer} />
            <button type="button" className={styles.secondaryButton} onClick={onClose}>
              {t('issue.cancel')}
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={submit}
              disabled={
                create.isPending ||
                blocked ||
                projectId === '' ||
                issueTypeId === '' ||
                (subtask && parent === null) ||
                summary.trim() === ''
              }
            >
              {create.isPending ? t('create.creating') : t('create.submit')}
            </button>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function clampProgress(value: number): number {
  return Number.isFinite(value) ? Math.min(Math.max(Math.round(value), 0), 100) : 0;
}
